// Trang CÔNG KHAI của một ảnh nền được chia sẻ — không cần đăng nhập.
//
// Chỉ có ảnh + nút tải. Ảnh là nội dung do người dùng gửi lên nên trang khai
// `noindex`: chia sẻ được bằng link, nhưng không vào chỉ mục tìm kiếm dưới tên
// miền của mình (BE cũng trả `X-Robots-Tag: noindex` cho chính tấm ảnh, và
// sitemap.xml KHÔNG chứa các link này).
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Download, ImageOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { downloadBlob } from "@/lib/share-image";
import { getPublicWallpaper, publicWallpaperImageUrl, type PublicWallpaper } from "@/lib/boide-api";

type LoaderData = { wallpaper: PublicWallpaper | null; token: string };

export const Route = createFileRoute("/anh-nen/$token")({
  loader: async ({ params }): Promise<LoaderData> => {
    try {
      return { wallpaper: await getPublicWallpaper(params.token), token: params.token };
    } catch {
      // Chủ ảnh đã thu hồi link (hoặc token sai) — trang tự hiện trạng thái
      // "không còn nữa", không phải màn hình lỗi.
      return { wallpaper: null, token: params.token };
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: `${loaderData?.wallpaper?.title || "Ảnh nền quẻ"} · Bói Đê` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PublicWallpaperPage,
});

function PublicWallpaperPage() {
  const { wallpaper, token } = Route.useLoaderData();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // `cache: "no-store"` để né bản cache no-cors mà thẻ <img> để lại — cùng
      // lý lẽ `shared-view.tsx`.
      const res = await fetch(publicWallpaperImageUrl(token), { cache: "no-store" });
      if (!res.ok) throw new Error("Không tải được ảnh");
      downloadBlob(await res.blob(), `boide-anh-nen-${token}.jpg`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Không tải được ảnh");
    } finally {
      setDownloading(false);
    }
  };

  if (!wallpaper) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-3 px-4 text-center">
        <ImageOff className="h-10 w-10 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Link này không còn nữa</h1>
        <p className="text-sm text-muted-foreground">
          Chủ ảnh đã thu hồi liên kết, hoặc đường dẫn không đúng.
        </p>
        <Button asChild variant="outline" className="mt-2">
          <a href="/kinh-dich">Tự gieo một quẻ</a>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-8">
      <div className="text-center">
        <h1 className="text-xl font-semibold">{wallpaper.title || "Ảnh nền quẻ Kinh Dịch"}</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {wallpaper.width}×{wallpaper.height} · chia sẻ từ boide.online
        </p>
      </div>

      <img
        src={publicWallpaperImageUrl(token)}
        alt={wallpaper.title || "Ảnh nền quẻ"}
        className="mx-auto mt-6 max-h-[70vh] w-auto rounded-xl border border-border/60 shadow-lg"
      />

      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button onClick={handleDownload} disabled={downloading}>
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Tải ảnh về
        </Button>
        <Button asChild variant="outline">
          <a href="/kinh-dich">Tự gieo quẻ của bạn</a>
        </Button>
      </div>
    </div>
  );
}
